<script lang="ts" setup>
import { ref, PropType, watch, onMounted, computed } from 'vue';
import { StoryItem } from '@/models/story-item.model';
import { Action } from '@/models/action.model';
import { Initiative } from '@/models/initiative.model';
import { useStore } from 'vuex';
import { ShiftCMSState } from '@/store';
import { StoriesState } from '@/stores/stories.store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';

const store = useStore<
  ShiftCMSState & { ts: StoriesState; cns: ContentStoreState }
>();
const props = defineProps({
  modelValue: { type: Object as PropType<StoryItem>, required: true },
});
const value = ref<StoryItem>({ ...props.modelValue });
const showAddAction = ref<boolean>(false);
const showAddInitiative = ref<boolean>(false);

const actions = computed<Action[]>(() => store.state.cns.actions);
const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

const addableAction = ref<Action[]>([]);
const addableInitiative = ref<Initiative[]>([]);

const emit = defineEmits(['update:modelValue']);

const update = () => {
  emit('update:modelValue', value.value);
};
watch(
  () => props.modelValue,
  () => {
    Object.assign(value.value, props.modelValue);
  },
  { deep: true },
);

onMounted(() => {
  store.dispatch(ContentStoreActions.GetActions);
  store.dispatch(ContentStoreActions.GetInitiatives);
});

function toggleAddAction() {
  showAddAction.value = !showAddAction.value;
}
function toggleAddInitiative() {
  showAddInitiative.value = !showAddInitiative.value;
}

function addAction() {
  if (addableAction.value) {
    value.value.actions = value.value.actions = [
      ...value.value.actions,
      ...addableAction.value,
    ];
    update();
  }
  addableAction.value = [];
}
function addInitiative() {
  if (addableInitiative.value) {
    value.value.initiatives = value.value.initiatives = [
      ...value.value.initiatives,
      ...addableInitiative.value,
    ];
    update();
  }
  addableInitiative.value = [];
}

function removeAction(id: number) {
  value.value.actions = [...value.value.actions].filter((a) => a.id !== id);
  update();
}
function removeInitiative(id: number) {
  value.value.initiatives = [...value.value.initiatives].filter(
    (a) => a.id !== id,
  );
  update();
}

const headers = [
  { title: 'Title', key: 'title', width: '70%' },
  { title: 'Options', key: 'options' },
];

const searchInitiative = ref<string>('');
const searchAction = ref<string>('');
</script>
<template>
  <v-row>
    <v-col>
      <h4>Actions</h4>
      <v-data-table :items="modelValue.actions" :headers="headers">
        <template
          v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }"
        >
          <tr>
            <template
              v-for="(column, index) in columns"
              :key="column.key || `c-${index}`"
            >
              <th>
                <span
                  class="mr-2 cursor-pointer"
                  @click="() => toggleSort(column)"
                  >{{ column.title }}</span
                >
                <template v-if="isSorted(column)">
                  <v-icon :icon="getSortIcon(column)"></v-icon>
                </template>

                <v-dialog
                  width="500px"
                  height="500px"
                  scrollable
                  v-if="column.title === 'Options'"
                >
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-btn
                      density="compact"
                      icon="mdi-plus"
                      @click="toggleAddAction()"
                      v-bind="activatorProps"
                    />
                  </template>
                  <template v-slot:default="{ isActive }">
                    <v-card>
                      <v-card-text>
                        <h4>Select an action to add</h4>
                        <v-text-field
                          label="Filter"
                          v-model="searchAction"
                          density="compact"
                          clearable
                        />
                        <v-radio-group
                          multiple
                          v-model="addableAction"
                          messages="Select an action to add"
                        >
                          <v-radio
                            v-for="action in [...actions]
                              .filter((i) =>
                                searchAction?.length
                                  ? i.title.toLowerCase().includes(searchAction)
                                  : true,
                              )
                              .filter(
                                (a) =>
                                  !value.actions
                                    .map((x) => x.id)
                                    .includes(a.id),
                              )"
                            :key="action.id"
                            :label="action.title"
                            :value="action"
                          ></v-radio>
                        </v-radio-group>
                      </v-card-text>

                      <v-divider></v-divider>
                      <v-card-actions>
                        <v-btn
                          text="Close"
                          @click="isActive.value = false"
                        ></v-btn>

                        <v-spacer></v-spacer>

                        <v-btn
                          color="surface-variant"
                          text="Add"
                          variant="flat"
                          @click="
                            addAction();
                            isActive.value = false;
                          "
                        ></v-btn>
                      </v-card-actions>
                    </v-card>
                  </template>
                </v-dialog>
              </th>
            </template>
          </tr>
        </template>
        <template v-slot:item.options="{ item }">
          <v-btn
            density="compact"
            icon="mdi-delete-outline"
            @click="removeAction(item.id)"
          />
        </template>
      </v-data-table>
    </v-col>
  </v-row>
  <v-row>
    <v-col>
      <h4>Initiatives</h4>
      <v-data-table :items="modelValue.initiatives" :headers="headers">
        <template
          v-slot:headers="{ columns, isSorted, getSortIcon, toggleSort }"
        >
          <tr>
            <template
              v-for="(column, index) in columns"
              :key="column.key || `c-${index}`"
            >
              <th>
                <span
                  class="mr-2 cursor-pointer"
                  @click="() => toggleSort(column)"
                  >{{ column.title }}</span
                >
                <template v-if="isSorted(column)">
                  <v-icon :icon="getSortIcon(column)"></v-icon>
                </template>

                <v-dialog
                  width="500px"
                  height="500px"
                  scrollable
                  v-if="column.title === 'Options'"
                >
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-btn
                      density="compact"
                      icon="mdi-plus"
                      @click="toggleAddInitiative()"
                      v-bind="activatorProps"
                    />
                  </template>
                  <template v-slot:default="{ isActive }">
                    <v-card>
                      <v-card-text>
                        <h4>Select an initiative to add</h4>
                        <v-text-field
                          label="Filter"
                          v-model="searchInitiative"
                          density="compact"
                          clearable
                        />
                        <v-radio-group multiple v-model="addableInitiative">
                          <v-radio
                            v-for="initiative in [...initiatives]
                              .filter((i) =>
                                searchInitiative?.length
                                  ? i.title
                                      .toLowerCase()
                                      .includes(searchInitiative)
                                  : true,
                              )
                              .filter(
                                (a) =>
                                  !value.initiatives
                                    .map((x) => x.id)
                                    .includes(a.id),
                              )"
                            :key="initiative.id"
                            :label="initiative.title"
                            :value="initiative"
                          ></v-radio>
                        </v-radio-group>
                      </v-card-text>

                      <v-divider></v-divider>
                      <v-card-actions>
                        <v-btn
                          text="Close"
                          @click="isActive.value = false"
                        ></v-btn>

                        <v-spacer></v-spacer>

                        <v-btn
                          color="surface-variant"
                          text="Add"
                          variant="flat"
                          @click="
                            addInitiative();
                            isActive.value = false;
                          "
                        ></v-btn>
                      </v-card-actions>
                    </v-card>
                  </template>
                </v-dialog>
              </th>
            </template>
          </tr>
        </template>
        <template v-slot:item.options="{ item }">
          <v-btn
            density="compact"
            icon="mdi-delete-outline"
            @click="removeInitiative(item.id)"
          />
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>
<style scoped>
.slider-number {
  width: 40px;
  text-align: center;
}
</style>
